import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Tabs, Tab, Card, Form, Button, Table, Alert, Modal, Badge, Pagination, ProgressBar } from 'react-bootstrap';
import { FaTrash, FaPlus, FaUpload, FaFolderPlus, FaKey, FaChartBar, FaDownload, FaUserLock, FaFolder } from 'react-icons/fa';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'https://wfshare.wishfin.com/api';
const API_KEY = process.env.REACT_APP_API_KEY || '';

const AdminDashboard = () => {
  const { user } = useAuth();

  // User management state
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ email: '', password: '', role: 'user' });
  const [userError, setUserError] = useState('');
  const [userSuccess, setUserSuccess] = useState('');
  
  // Password reset state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserEmail, setResetUserEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  
  // File management state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [uploadFolder, setUploadFolder] = useState('');
  const [newFolderPath, setNewFolderPath] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [uploadTimeRemaining, setUploadTimeRemaining] = useState(null);
  const uploadStartTimeRef = useRef(null);

  const formatBytes = (bytes, decimals = 2) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  const formatTimeRemaining = (seconds) => {
    if (seconds === null || seconds === undefined) return 'Calculating...';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hrs > 0) return `${hrs}h ${mins}m ${secs}s remaining`;
    if (mins > 0) return `${mins}m ${secs}s remaining`;
    return `${secs}s remaining`;
  };

  const trackUploadProgress = (progressEvent) => {
    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    setUploadProgress(percentCompleted);
    setUploadedBytes(progressEvent.loaded);
    setTotalBytes(progressEvent.total);

    const elapsedSeconds = (Date.now() - uploadStartTimeRef.current) / 1000;
    if (elapsedSeconds > 0.2) {
      const speed = progressEvent.loaded / elapsedSeconds;
      setUploadSpeed(speed);
      const remainingBytes = progressEvent.total - progressEvent.loaded;
      setUploadTimeRemaining(speed > 0 ? Math.round(remainingBytes / speed) : null);
    }
  };
  
  // Download statistics state
  const [downloadStats, setDownloadStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  // Folder permissions state
  const [folderPermissions, setFolderPermissions] = useState([]);
  const [newFolderPermission, setNewFolderPermission] = useState({ folder_path: '', user_id: '' });
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionsError, setPermissionsError] = useState('');
  const [permissionsSuccess, setPermissionsSuccess] = useState('');
  const [existingFolders, setExistingFolders] = useState([]);

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
    fetchDownloadStats();
    fetchFolderPermissions();
    fetchExistingFolders();
  }, []);

  // Recursively list every folder in the bucket (as an admin) so the folder
  // permission form can offer exact, typo-proof paths instead of requiring
  // the admin to retype the path they used in "Create Folder"
  const listFoldersRecursive = async (prefix, allPaths) => {
    const response = await axios.get(
      `${API_URL}/list`,
      {
        params: { prefix, user_id: user?.id },
        headers: { 'x-api-key': API_KEY }
      }
    );

    const folders = response.data.folders || [];
    for (const folder of folders) {
      allPaths.push(folder.path);
      await listFoldersRecursive(folder.path, allPaths);
    }
  };

  const fetchExistingFolders = async () => {
    try {
      const allPaths = [];
      await listFoldersRecursive('', allPaths);
      setExistingFolders([...new Set(allPaths)].sort());
    } catch (err) {
      // Non-critical: the form still works as free text if this fails
      console.error('Failed to load existing folders for autocomplete', err);
    }
  };
  
  const fetchUsers = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/admin/users`,
        {
          headers: {
            'x-api-key': API_KEY
          }
        }
      );
      
      setUsers(response.data.users || []);
    } catch (err) {
      setUserError('Failed to load users');
    }
  };
  
  const fetchDownloadStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError('');
      
      const response = await axios.get(
        `${API_URL}/admin/download-stats`,
        {
          headers: {
            'x-api-key': API_KEY
          }
        }
      );
      
      setDownloadStats(response.data.stats || []);
      setStatsLoading(false);
    } catch (err) {
      setStatsError('Failed to load download statistics');
      setStatsLoading(false);
    }
  };
  
  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };
  
  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    try {
      setUserError('');
      setUserSuccess('');
      
      await axios.post(
        `${API_URL}/admin/users`,
        newUser,
        {
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Reset form and refresh users
      setNewUser({ email: '', password: '', role: 'user' });
      setUserSuccess('User created successfully');
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to create user';
      setUserError(errorMessage);
    }
  };
  
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) {
      return;
    }
    
    try {
      setUserError('');
      setUserSuccess('');
      
      await axios.delete(
        `${API_URL}/admin/users/${userId}`,
        {
          headers: {
            'x-api-key': API_KEY
          }
        }
      );
      
      setUserSuccess('User deleted successfully');
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to delete user';
      setUserError(errorMessage);
    }
  };

  const handleRoleChange = async (userId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'user' : 'admin';
    const action = newRole === 'admin' ? 'promote this user to admin' : 'demote this user to a regular user';
    if (!window.confirm(`Are you sure you want to ${action}?`)) {
      return;
    }

    try {
      setUserError('');
      setUserSuccess('');

      await axios.put(
        `${API_URL}/admin/users/${userId}/role`,
        { role: newRole },
        {
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          }
        }
      );

      setUserSuccess(`User role updated to ${newRole}`);
      fetchUsers();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to update user role';
      setUserError(errorMessage);
    }
  };

  const openResetPasswordModal = (userId, userEmail) => {
    setResetUserId(userId);
    setResetUserEmail(userEmail);
    setNewPassword('');
    setShowResetModal(true);
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    try {
      setUserError('');
      setUserSuccess('');
      
      const formData = new FormData();
      formData.append('password', newPassword);
      
      await axios.put(
        `${API_URL}/admin/users/${resetUserId}/reset-password`,
        formData,
        {
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setUserSuccess('Password reset successfully');
      setShowResetModal(false);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to reset password';
      setUserError(errorMessage);
    }
  };
  
  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };
  
  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      setUploadError('Please select a file');
      return;
    }
    
    try {
      setUploadError('');
      setUploadSuccess('');
      setUploadProgress(0);
      setUploadedBytes(0);
      setTotalBytes(selectedFile.size);
      setUploadSpeed(0);
      setUploadTimeRemaining(null);
      uploadStartTimeRef.current = Date.now();
      setUploading(true);

      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('folder', uploadFolder);

      await axios.post(
        `${API_URL}/admin/upload`,
        formData,
        {
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: trackUploadProgress
        }
      );

      setUploadSuccess('File uploaded successfully');
      setSelectedFile(null);
      setShowUploadModal(false);

      // Reset file input
      const fileInput = document.getElementById('fileInput');
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to upload file';
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleCreateFolder = async (e) => {
    e.preventDefault();
    
    if (!newFolderPath) {
      setUploadError('Please enter a folder path');
      return;
    }
    
    try {
      setUploadError('');
      setUploadSuccess('');
      
      const formData = new FormData();
      formData.append('folder_path', newFolderPath);
      
      await axios.post(
        `${API_URL}/admin/createfolder`,
        formData,
        {
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setUploadSuccess('Folder created successfully');
      setNewFolderPath('');
      setShowFolderModal(false);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to create folder';
      setUploadError(errorMessage);
    }
  };
  
  // Folder permissions functions
  const fetchFolderPermissions = async () => {
    try {
      setPermissionsLoading(true);
      setPermissionsError('');
      
      const response = await axios.get(
        `${API_URL}/admin/folder-permissions`,
        {
          headers: {
            'x-api-key': API_KEY
          }
        }
      );
      
      setFolderPermissions(response.data.permissions || []);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to load folder permissions';
      setPermissionsError(errorMessage);
    } finally {
      setPermissionsLoading(false);
    }
  };
  
  const handleNewFolderPermissionChange = (e) => {
    const { name, value } = e.target;
    setNewFolderPermission(prev => ({ ...prev, [name]: value }));
  };
  
  const handleAssignFolderToUser = async (e) => {
    e.preventDefault();
    
    if (!newFolderPermission.folder_path || !newFolderPermission.user_id) {
      setPermissionsError('Please enter both folder path and select a user');
      return;
    }
    
    try {
      setPermissionsError('');
      setPermissionsSuccess('');
      
      await axios.post(
        `${API_URL}/admin/folder-permissions`,
        newFolderPermission,
        {
          headers: {
            'x-api-key': API_KEY,
            'Content-Type': 'application/json'
          }
        }
      );
      
      setPermissionsSuccess('Folder assigned to user successfully');
      setNewFolderPermission({ folder_path: '', user_id: '' });
      fetchFolderPermissions();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to assign folder to user';
      setPermissionsError(errorMessage);
    }
  };
  
  const handleDeleteFolderPermission = async (permissionId) => {
    if (!window.confirm('Are you sure you want to remove this folder permission?')) {
      return;
    }
    
    try {
      setPermissionsError('');
      setPermissionsSuccess('');
      
      await axios.delete(
        `${API_URL}/admin/folder-permissions/${permissionId}`,
        {
          headers: {
            'x-api-key': API_KEY
          }
        }
      );
      
      setPermissionsSuccess('Folder permission removed successfully');
      fetchFolderPermissions();
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to remove folder permission';
      setPermissionsError(errorMessage);
    }
  };
  
  return (
    <Container>
      <h2 className="mb-4">Admin Dashboard</h2>
      
      <Tabs defaultActiveKey="users" className="mb-4">
        <Tab eventKey="users" title="User Management">
          <Card>
            <Card.Body>
              <h4>Create New User</h4>
              
              {userError && <Alert variant="danger">{userError}</Alert>}
              {userSuccess && <Alert variant="success">{userSuccess}</Alert>}
              
              <Form onSubmit={handleCreateUser}>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={newUser.email}
                        onChange={handleNewUserChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3" controlId="password">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="text"
                        name="password"
                        value={newUser.password}
                        onChange={handleNewUserChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3" controlId="role">
                      <Form.Label>Role</Form.Label>
                      <Form.Select
                        name="role"
                        value={newUser.role}
                        onChange={handleNewUserChange}
                      >
                        <option value="user">User</option>
                        <option value="admin">Admin</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={1} className="d-flex align-items-end mb-3">
                    <Button type="submit" variant="primary">
                      <FaPlus /> Add
                    </Button>
                  </Col>
                </Row>
              </Form>
              
              <hr />
              
              <h4 className="mb-3">Users</h4>
              
              <Table striped bordered hover className="user-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.email}</td>
                      <td>
                        <Badge bg={user.role === 'admin' ? 'primary' : 'secondary'}>
                          {user.role}
                        </Badge>
                      </td>
                      <td>
                        <Button
                          variant={user.role === 'admin' ? 'outline-secondary' : 'outline-primary'}
                          size="sm"
                          className="me-2"
                          onClick={() => handleRoleChange(user.id, user.role)}
                        >
                          <FaUserLock /> {user.role === 'admin' ? 'Demote to User' : 'Promote to Admin'}
                        </Button>
                        <Button
                          variant="warning"
                          size="sm"
                          className="me-2"
                          onClick={() => openResetPasswordModal(user.id, user.email)}
                        >
                          <FaKey /> Reset Password
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id)}
                        >
                          <FaTrash /> Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan="4" className="text-center">No users found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="files" title="File Management">
          <Card>
            <Card.Body>
              <h4>File Operations</h4>
              
              {uploadSuccess && <Alert variant="success">{uploadSuccess}</Alert>}
              
              <Row>
                <Col md={6} className="mb-3">
                  <Button
                    variant="primary"
                    className="me-2"
                    onClick={() => setShowUploadModal(true)}
                  >
                    <FaUpload /> Upload File
                  </Button>
                  
                  <Button
                    variant="secondary"
                    onClick={() => setShowFolderModal(true)}
                  >
                    <FaFolderPlus /> Create Folder
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="permissions" title={<><FaUserLock /> Folder Permissions</>}>
          <Card>
            <Card.Body>
              <h4>Assign Folders to Users</h4>
              
              {permissionsError && <Alert variant="danger">{permissionsError}</Alert>}
              {permissionsSuccess && <Alert variant="success">{permissionsSuccess}</Alert>}
              
              <Form onSubmit={handleAssignFolderToUser}>
                <Row>
                  <Col md={5}>
                    <Form.Group className="mb-3" controlId="folder_path">
                      <Form.Label>Folder Path</Form.Label>
                      <Form.Control
                        type="text"
                        name="folder_path"
                        list="existing-folders-list"
                        value={newFolderPermission.folder_path}
                        onChange={handleNewFolderPermissionChange}
                        placeholder="e.g. documents/"
                        required
                      />
                      <datalist id="existing-folders-list">
                        {existingFolders.map(path => (
                          <option key={path} value={path} />
                        ))}
                      </datalist>
                      <Form.Text className="text-muted">
                        Pick an existing folder from the list, or type an exact path. Don't forget the trailing slash.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-3" controlId="user_id">
                      <Form.Label>User</Form.Label>
                      <Form.Select
                        name="user_id"
                        value={newFolderPermission.user_id}
                        onChange={handleNewFolderPermissionChange}
                        required
                      >
                        <option value="">-- Select User --</option>
                        {users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.email} ({user.role})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={2} className="d-flex align-items-end mb-3">
                    <Button type="submit" variant="primary">
                      <FaPlus /> Assign
                    </Button>
                  </Col>
                </Row>
              </Form>
              
              <hr />
              
              <h4>Current Folder Permissions</h4>
              
              {permissionsLoading ? (
                <div className="text-center p-4">Loading permissions...</div>
              ) : (
                <Table striped bordered hover>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Folder Path</th>
                      <th>User</th>
                      <th>Assigned At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {folderPermissions.map(permission => (
                      <tr key={permission.id}>
                        <td>{permission.id}</td>
                        <td>
                          <FaFolder className="me-1" />
                          {permission.folder_path}
                        </td>
                        <td>{permission.email}</td>
                        <td>{new Date(permission.created_at).toLocaleString()}</td>
                        <td>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteFolderPermission(permission.id)}
                          >
                            <FaTrash /> Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {folderPermissions.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center">No folder permissions found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
        
        <Tab eventKey="stats" title={<><FaChartBar /> Statistics</>}>
          <Card>
            <Card.Body>
              <h4>Download Statistics</h4>
              
              {statsError && <Alert variant="danger">{statsError}</Alert>}
              
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button 
                  variant="outline-secondary" 
                  size="sm"
                  onClick={fetchDownloadStats}
                >
                  <FaChartBar /> Refresh Statistics
                </Button>
                
                <Form.Group as={Row} className="items-per-page">
                  <Form.Label column sm="auto">Items per page:</Form.Label>
                  <Col sm="auto">
                    <Form.Select 
                      size="sm" 
                      value={itemsPerPage}
                      onChange={e => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </Form.Select>
                  </Col>
                </Form.Group>
              </div>
              
              {statsLoading ? (
                <div className="text-center p-4">Loading statistics...</div>
              ) : (
                <>
                  <Table responsive striped bordered hover className="stats-table">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Path</th>
                        <th>Downloaded By</th>
                        <th>Download Count</th>
                        <th>First Downloaded</th>
                        <th>Last Downloaded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {downloadStats
                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                        .map((stat) => (
                          <tr key={stat.id}>
                            <td>{stat.file_name}</td>
                            <td>{stat.file_path}</td>
                            <td>{stat.downloaded_by}</td>
                            <td>
                              <Badge bg="primary" pill>
                                <FaDownload className="me-1" />
                                {stat.count}
                              </Badge>
                            </td>
                            <td>{new Date(stat.first_downloaded_at).toLocaleString()}</td>
                            <td>{new Date(stat.last_downloaded_at).toLocaleString()}</td>
                          </tr>
                      ))}
                      {downloadStats.length === 0 && (
                        <tr>
                          <td colSpan="6" className="text-center">No download statistics found</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                  
                  {downloadStats.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        Showing {Math.min((currentPage - 1) * itemsPerPage + 1, downloadStats.length)} to {Math.min(currentPage * itemsPerPage, downloadStats.length)} of {downloadStats.length} entries
                      </div>
                      <Pagination>
                        <Pagination.First 
                          onClick={() => setCurrentPage(1)} 
                          disabled={currentPage === 1}
                        />
                        <Pagination.Prev 
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                          disabled={currentPage === 1}
                        />
                        
                        {[...Array(Math.ceil(downloadStats.length / itemsPerPage))].map((_, i) => {
                          // Show current page, 1 before, 1 after, first and last
                          if (
                            i + 1 === 1 || 
                            i + 1 === Math.ceil(downloadStats.length / itemsPerPage) || 
                            Math.abs(i + 1 - currentPage) <= 1
                          ) {
                            return (
                              <Pagination.Item 
                                key={i} 
                                active={i + 1 === currentPage}
                                onClick={() => setCurrentPage(i + 1)}
                              >
                                {i + 1}
                              </Pagination.Item>
                            );
                          } else if (
                            i + 1 === currentPage - 2 || 
                            i + 1 === currentPage + 2
                          ) {
                            return <Pagination.Ellipsis key={i} />;
                          }
                          return null;
                        })}
                        
                        <Pagination.Next 
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(downloadStats.length / itemsPerPage)))} 
                          disabled={currentPage === Math.ceil(downloadStats.length / itemsPerPage)}
                        />
                        <Pagination.Last 
                          onClick={() => setCurrentPage(Math.ceil(downloadStats.length / itemsPerPage))} 
                          disabled={currentPage === Math.ceil(downloadStats.length / itemsPerPage)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
      
      {/* Upload File Modal */}
      <Modal show={showUploadModal} onHide={() => {
        if (uploading) return;
        setShowUploadModal(false);
        setUploadError('');
        setUploadProgress(0);
        setUploadedBytes(0);
        setUploadSpeed(0);
        setUploadTimeRemaining(null);
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Upload File</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadError && <Alert variant="danger">{uploadError}</Alert>}

          <Form onSubmit={handleFileUpload}>
            <Form.Group className="mb-3" controlId="folder">
              <Form.Label>Folder (optional)</Form.Label>
              <Form.Control
                type="text"
                value={uploadFolder}
                onChange={(e) => setUploadFolder(e.target.value)}
                placeholder="e.g. documents/ (leave empty for root)"
                disabled={uploading}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="fileInput">
              <Form.Label>Select File</Form.Label>
              <Form.Control
                type="file"
                onChange={handleFileSelect}
                disabled={uploading}
                required
              />
            </Form.Group>

            {uploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <span className="fw-semibold">{selectedFile?.name}</span>
                  <span className="text-muted">{formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}</span>
                </div>
                <ProgressBar
                  now={uploadProgress}
                  label={`${uploadProgress}%`}
                  animated
                />
                <div className="d-flex justify-content-between mt-2 text-muted small">
                  <span>{formatBytes(uploadSpeed)}/s</span>
                  <span>{uploadProgress >= 100 ? 'Finishing up...' : formatTimeRemaining(uploadTimeRemaining)}</span>
                </div>
              </div>
            )}

            <Button type="submit" variant="primary" disabled={uploading}>
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Create Folder Modal */}
      <Modal show={showFolderModal} onHide={() => setShowFolderModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Create Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {uploadError && <Alert variant="danger">{uploadError}</Alert>}
          
          <Form onSubmit={handleCreateFolder}>
            <Form.Group className="mb-3" controlId="folderPath">
              <Form.Label>Folder Path</Form.Label>
              <Form.Control
                type="text"
                value={newFolderPath}
                onChange={(e) => setNewFolderPath(e.target.value)}
                placeholder="e.g. documents/new-folder/"
                required
              />
            </Form.Group>
            
            <Button type="submit" variant="primary">
              Create
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
      
      {/* Reset Password Modal */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {userError && <Alert variant="danger">{userError}</Alert>}
          
          <Form onSubmit={handleResetPassword}>
            <p>Reset password for user: <strong>{resetUserEmail}</strong></p>
            
            <Form.Group className="mb-3" controlId="newPassword">
              <Form.Label>New Password</Form.Label>
              <Form.Control
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                required
              />
            </Form.Group>
            
            <Button type="submit" variant="warning">
              <FaKey /> Reset Password
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminDashboard;
